import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import AttachIcon from 'assets/chat/ActionIcon/attach.svg';
import EmojiBlackIcon from 'assets/chat/ActionIcon/emojiBlack.svg';
import SendIcon from 'assets/chat/ActionIcon/send.svg';
import EmojiPicker from 'emoji-picker-react';
import {
  getUnreadCount,
  markConversationAsRead,
  markMessageAsRead,
  sendMessage,
} from 'features/chat/chatActions';
import { selectSendingMessage } from 'features/chat/chatSlice';
import { useFacebookMessagesInfiniteScroll } from 'hooks/useChatInfiniteScroll';

import {
  ActionsAttachButton,
  AttachButtonIcon,
  ChatInput,
  ChatInputContainer,
  ChatInputContainerActions,
  ChatInputContainerFlex,
  ChatSingleBlock,
  ChatSingleContent,
  EmojiButtonIcon,
  EmojiPickerWrapper,
  HiddenFileInput,
  ChatSingleItemLeft as LoadingMessageContainer,
  ChatSingleItemLeftContent as LoadingMessageContent,
  ChatSingleItemLeftMessageText as LoadingMessageText,
  SendButton,
  SendButtonIcon,
  SendButtonSpinner,
} from './ChatSingle.styles';
import ChatSingleItemLeft from './components/ChatSingleItemLeft';
import ChatSingleItemRight from './components/ChatSingleItemRight';
import FilePreviewContainerComponent from './components/FilePreviewContainer';
import { BUSINESS_ACCOUNT_ID, getRecipientId } from './utils/chatUtils';

const FacebookChatSingle = ({ conversation, platform, permissionType }) => {
  const dispatch = useDispatch();
  const isSending = useSelector(selectSendingMessage);
  const isViewOnly = permissionType === 'view_only';

  const recipientId = getRecipientId(conversation);

  const userId = recipientId;

  const { messages, loading, hasNextPage, loadOlder } = useFacebookMessagesInfiniteScroll(
    userId,
    20
  );
  // console.log(conversation.conversationId);
  const [value, setValue] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState({});
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const textareaRef = useRef(null);
  const contentRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const previousScrollHeightRef = useRef(0);
  const previousScrollTopRef = useRef(0);
  const markedAsReadRef = useRef(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '36px';
      const style = window.getComputedStyle(textarea);
      const padding = parseInt(style.paddingTop) + parseInt(style.paddingBottom);
      const newHeight = Math.min(textarea.scrollHeight - padding, 71);
      textarea.style.height = `${Math.max(newHeight, 36)}px`;
    }
  }, [value]);

  useEffect(() => {
    if (messages.length > 0 && isInitialLoadRef.current && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
      isInitialLoadRef.current = false;
    }
  }, [messages]);

  /**
   * Maintain scroll position when prepending older messages at the top
   */
  useEffect(() => {
    if (isLoadingOlder && contentRef.current) {
      requestAnimationFrame(() => {
        if (!contentRef.current) return;

        const currentScrollHeight = contentRef.current.scrollHeight;
        const heightDifference = currentScrollHeight - previousScrollHeightRef.current;

        if (heightDifference > 0) {
          contentRef.current.scrollTop = previousScrollTopRef.current + heightDifference;
        }

        setIsLoadingOlder(false);
      });
    }
  }, [messages, isLoadingOlder]);

  useEffect(() => {
    Object.values(filePreviewUrls).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    setSelectedFiles([]);
    setFilePreviewUrls({});
    isInitialLoadRef.current = true;
    markedAsReadRef.current.clear();

    if (recipientId) {
      dispatch(markConversationAsRead({ conversationId: recipientId }));
    }
  }, [conversation?.conversationId]);

  const markAsRead = useCallback(
    (messageId) => {
      if (!messageId || markedAsReadRef.current.has(messageId)) {
        return;
      }

      markedAsReadRef.current.add(messageId);
      dispatch(markMessageAsRead({ messageId }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!contentRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.dataset.messageId;
            const isIncoming = entry.target.dataset.isIncoming === 'true';
            const isUnread = entry.target.dataset.isUnread === 'true';

            if (messageId && isIncoming && isUnread) {
              markAsRead(messageId);
            }
          }
        });
      },
      {
        root: contentRef.current,
        threshold: 0.5,
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [markAsRead]);

  useEffect(() => {
    return () => {
      Object.values(filePreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [filePreviewUrls]);

  /**
   * Handle scroll to load older messages when reaching the top
   */
  const handleScroll = useCallback(() => {
    const element = contentRef.current;
    if (!element || loading || isLoadingOlder) return;

    const scrollTop = element.scrollTop;

    // Load older messages when scrolling near the top (within 100px)
    if (scrollTop < 100 && hasNextPage) {
      setIsLoadingOlder(true);
      previousScrollHeightRef.current = element.scrollHeight;
      previousScrollTopRef.current = scrollTop;
      loadOlder();
    }
  }, [loading, hasNextPage, loadOlder, isLoadingOlder]);

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const transformAttachments = (fbAttachments) => {
    if (!fbAttachments || fbAttachments?.length === 0) {
      return [];
    }

    return fbAttachments
      ?.map((attachment) => {
        if (attachment.type === 'image') {
          return {
            type: 'image',
            payload: {
              url: attachment.url,
              preview_url: attachment.url,
              // width: attachment.image_data.width,
              // height: attachment.image_data.height,
            },
          };
        }

        if (attachment.type === 'video') {
          return {
            type: 'video',
            payload: {
              url: attachment.url,
              preview_url: attachment.url,
            },
          };
        }

        if (attachment.type === 'file') {
          return {
            type: 'file',
            payload: {
              url: attachment.url,
              name: attachment.name || 'Download File',
              size: attachment.size,
            },
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const transformMessage = useCallback((fbMessage) => {
    if (fbMessage.direction) {
      return {
        _id: fbMessage._id || fbMessage.messageId,
        messageId: fbMessage.messageId,
        messageText:
          fbMessage.messageText ||
          (typeof fbMessage.message === 'string'
            ? fbMessage.message
            : fbMessage.message?.text) ||
          '',
        sender: fbMessage.sender,
        sentBy: fbMessage.sentBy,
        createdAt: fbMessage.createdAt || fbMessage.timestamp,
        direction: fbMessage.direction,
        platform: fbMessage.platform || 'facebook',
        attachments: fbMessage.attachments,
        isRead: true,
        _raw: fbMessage,
      };
    }
    const isFromPage =
      fbMessage.from?.id === '176902635512677' || fbMessage.from?.id === BUSINESS_ACCOUNT_ID;

    const attachments = transformAttachments(fbMessage.attachments);

    return {
      _id: fbMessage.id,
      messageId: fbMessage.id,
      messageText: fbMessage.message || '',
      sender: fbMessage.from,
      sentBy: fbMessage.sentBy,
      createdAt: fbMessage.created_time,
      direction: isFromPage ? 'outgoing' : 'incoming',
      platform: 'facebook',
      attachments: attachments.length > 0 ? attachments : undefined,
      isRead: true,
      _raw: fbMessage,
    };
  }, []);

  const getFileType = (file) => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'file';
  };

  const handleSendMessage = async () => {
    if (isViewOnly) return;
    if ((!value.trim() && selectedFiles.length === 0) || isSending || !recipientId) {
      console.warn('Cannot send message: missing content or recipient ID');
      return;
    }

    try {
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const messageData = {
            conversationId: recipientId,
            platform: 'facebook',
            messageText: selectedFiles.length === 1 ? value.trim() || undefined : undefined,
            file: file,
            fileType: getFileType(file),
          };
          await dispatch(sendMessage(messageData)).unwrap();
        }

        if (selectedFiles.length > 1 && value.trim()) {
          const messageData = {
            conversationId: recipientId,
            platform: 'facebook',
            messageText: value.trim(),
          };
          await dispatch(sendMessage(messageData)).unwrap();
        }
      } else {
        const messageData = {
          conversationId: recipientId,
          platform: 'facebook',
          messageText: value.trim() || undefined,
        };
        await dispatch(sendMessage(messageData)).unwrap();
      }

      setValue('');
      Object.values(filePreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      setSelectedFiles([]);
      setFilePreviewUrls({});
      dispatch(getUnreadCount());
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + emoji + value.substring(end);
      setValue(newValue);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setValue((prev) => prev + emoji);
    }
  };

  const toggleEmojiPicker = () => {
    if (isViewOnly) return;
    setShowEmojiPicker((prev) => !prev);
  };

  const handleAttachClick = () => {
    if (isViewOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    if (isViewOnly) return;
    const files = Array.from(e.target.files || []);

    if (files.length > 0) {
      const newFiles = [...selectedFiles, ...files];
      setSelectedFiles(newFiles);

      files.forEach((file, index) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreviewUrls((prev) => ({
              ...prev,
              [selectedFiles.length + index]: reader.result,
            }));
          };
          reader.readAsDataURL(file);
        }
      });
    }

    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    if (filePreviewUrls[index]) {
      URL.revokeObjectURL(filePreviewUrls[index]);
    }

    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);

    const newPreviewUrls = {};
    Object.entries(filePreviewUrls).forEach(([key, value]) => {
      const oldIndex = parseInt(key);
      if (oldIndex < index) {
        newPreviewUrls[oldIndex] = value;
      } else if (oldIndex > index) {
        newPreviewUrls[oldIndex - 1] = value;
      }
    });
    setFilePreviewUrls(newPreviewUrls);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('.emoji-picker-button')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  if (loading && messages.length === 0) {
    return (
      <ChatSingleBlock>
        <ChatSingleContent>
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>Loading messages...</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        </ChatSingleContent>
      </ChatSingleBlock>
    );
  }
  // console.log(messages);
  return (
    <ChatSingleBlock>
      <ChatSingleContent ref={contentRef}>
        {/* Loading indicator at top when loading older messages */}
        {hasNextPage && isLoadingOlder && (
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>Loading older messages...</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        )}

        {messages.length === 0 ? (
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>No messages yet</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        ) : (
          messages.map((fbMessage) => {
            const message = transformMessage(fbMessage);
            const isMyMessage = message.direction === 'outgoing';
            const messageId = message.messageId || message._id || message.id;
            const isUnread = !message.isRead && !isMyMessage;

            return (
              <div
                key={message._id}
                data-message-id={messageId}
                data-is-incoming={!isMyMessage}
                data-is-unread={isUnread}
                ref={(el) => {
                  if (el && observerRef.current && !isMyMessage && isUnread) {
                    observerRef.current.observe(el);
                  }
                }}
                onClick={() => {
                  if (!isMyMessage && isUnread) {
                    markAsRead(messageId);
                  }
                }}
                style={{ cursor: !isMyMessage && isUnread ? 'pointer' : 'default' }}
              >
                {!isMyMessage ? (
                  <ChatSingleItemLeft
                    message={message}
                    conversation={conversation}
                    platform={platform}
                  />
                ) : (
                  <ChatSingleItemRight message={message} platform={platform} />
                )}
              </div>
            );
          })
        )}
      </ChatSingleContent>

      <FilePreviewContainerComponent
        selectedFiles={selectedFiles}
        filePreviewUrls={filePreviewUrls}
        onRemove={handleRemoveFile}
      />

      {!isViewOnly && (
        <ChatInputContainer>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
          />
          <ChatInputContainerFlex>
            <ChatInputContainerActions>
              <ActionsAttachButton onClick={handleAttachClick}>
                <AttachButtonIcon src={AttachIcon} />
              </ActionsAttachButton>
              <ActionsAttachButton onClick={toggleEmojiPicker} className="emoji-picker-button">
                <EmojiButtonIcon src={EmojiBlackIcon} />
              </ActionsAttachButton>
            </ChatInputContainerActions>
            <ChatInput
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={isSending}
            />
            <SendButton onClick={handleSendMessage} disabled={isSending}>
              {isSending ? <SendButtonSpinner /> : <SendButtonIcon src={SendIcon} />}
            </SendButton>
          </ChatInputContainerFlex>
          {showEmojiPicker && (
            <EmojiPickerWrapper ref={emojiPickerRef}>
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                width={320}
                height={400}
                searchDisabled={false}
                skinTonesDisabled={false}
                previewConfig={{
                  showPreview: false,
                }}
              />
            </EmojiPickerWrapper>
          )}
        </ChatInputContainer>
      )}

      {isViewOnly && (
        <ChatInputContainer>
          <LoadingMessageText style={{ textAlign: 'center', padding: '16px' }}>
            You have view-only access to this conversation
          </LoadingMessageText>
        </ChatInputContainer>
      )}
    </ChatSingleBlock>
  );
};

export default FacebookChatSingle;
